// Wall Mode runtime (React port). Paged panels, runs MQTT action sequences,
// keeps the screen awake (Screen Wake Lock API), tap-to-wake black overlay, and
// a PIN-guarded exit. Landscape is requested where the browser allows it.

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, IconButton, Button, CircularProgress } from '@mui/material';
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew';
import DashboardCustomizeIcon from '@mui/icons-material/DashboardCustomize';
import CloseIcon from '@mui/icons-material/Close';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useApp } from '../../../../core/state/AppContext';
import { fetchPanels, runSequence } from './wallApi';
import {
  panelsForLocation, parseLocationPanel, isLocationPanel, normaliseSize,
  columnsFor, rowsFor, layoutButtons,
} from '../../../../core/wall/wallLocation';
import { loadKiosk } from '../../../../core/wall/kioskMode';
import {
  toggleTarget, isToggleOn, pressButton, fanTarget, fanSpeedOf, fanStepsFor,
  curtainTarget, lockTarget, lightTarget, lightPresetsFor, CURTAIN_ACTIONS,
} from '../../../../core/wall/wallButtons';
import { publishCommand } from '../../../../core/utils/deviceController';
import LockIcon from '@mui/icons-material/Lock';
import { connectMqtt, subscribeTopic, unsubscribeTopic, addMessageListener } from '../../../../core/mqtt/mqttClient';

// One slot holding several actions — a fan ladder, or Open/Stop/Close. The
// alternative is giving each action its own slot, which on a 4-button sheet
// spends the whole panel on one curtain.
function Cluster({
  button, steps, columns, rows, activeIndex = -1, onStep,
}) {
  return (
    <Box sx={{
      bgcolor: button.ButtonColor || '#37474F', color: button.TextColor || '#fff',
      borderRadius: 2, p: 0.75, minHeight: 0, minWidth: 0,
      display: 'flex', flexDirection: 'column', overflow: 'hidden',
    }}
    >
      <Box sx={{
        fontWeight: 700, fontSize: 'clamp(10px, 1.4vw, 15px)', textAlign: 'center',
        mb: 0.5, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
      }}
      >
        {button.ButtonText}
      </Box>
      <Box sx={{
        flex: 1, minHeight: 0, display: 'grid', gap: '3px',
        gridTemplateColumns: `repeat(${columns}, 1fr)`,
        gridTemplateRows: `repeat(${rows}, 1fr)`,
      }}
      >
        {steps.map((st, i) => (
          <Box
            key={st.message}
            onClick={() => onStep(st.message)}
            sx={{
              borderRadius: 1, display: 'flex', alignItems: 'center',
              justifyContent: 'center', cursor: 'pointer', userSelect: 'none',
              fontSize: 'clamp(9px, 1.2vw, 14px)', fontWeight: 700,
              minHeight: 0, minWidth: 0,
              // The running step is filled; the rest outlined. Unknown lights
              // nothing, the same rule a toggle follows.
              bgcolor: i === activeIndex ? 'rgba(255,255,255,0.92)' : 'transparent',
              color: i === activeIndex ? '#111' : 'inherit',
              border: i === activeIndex ? 'none' : '1px solid rgba(255,255,255,0.35)',
            }}
          >
            {st.label}
          </Box>
        ))}
      </Box>
    </Box>
  );
}

export default function WallModeScreen() {
  const { userId, user, colors } = useApp();
  const navigate = useNavigate();
  const [panels, setPanels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [screenOff, setScreenOff] = useState(false);
  const wakeRef = useRef(null);

  // On a kiosk tablet, show ONLY that location's panels. Otherwise show the
  // personal ones — a location sheet is a wall fixture and would just be noise
  // in someone's phone pager.
  const load = useCallback(async () => {
    setLoading(true);
    const all = await fetchPanels(userId);
    const k = loadKiosk();
    setPanels(k.enabled && k.locationId != null
      ? panelsForLocation(all, k.locationId)
      : all.filter((p) => !isLocationPanel(p)));
    setLoading(false);
  }, [userId]);

  useEffect(() => { load(); }, [load]);

  // Live state for the toggle buttons on show.
  //
  // Subscribes ONLY to the devices this sheet actually toggles — a wall panel
  // that watched everything would carry the whole broker for the sake of a few
  // lit buttons. Momentary buttons need nothing: a curtain does not report
  // "open" and a scene has no state to show.
  const [statuses, setStatuses] = useState({});   // uno -> latest status text
  const [lockAsk, setLockAsk] = useState(null);  // { button, target } awaiting a password

  // ⚠️ IN AN EFFECT, NOT IN RENDER. window.prompt blocks and has a side effect;
  // running it while rendering fires it during reconciliation — twice under
  // StrictMode — and a door that asks for its password twice, or opens on a
  // render, is not a door lock.
  //
  // window.prompt for the same reason exit uses it: a wall tablet has no way
  // past a modal browser prompt, which is exactly what is wanted here.
  useEffect(() => {
    if (!lockAsk) return;
    const entered = window.prompt(`Password to open ${lockAsk.button.ButtonText || 'the lock'}`);
    setLockAsk(null);
    if (entered == null) return;
    if (entered === String(user?.Password)) {
      publishCommand(lockAsk.target.uniqueNumber, lockAsk.target.message, userId);
    } else {
      window.alert('Wrong password');
    }
  }, [lockAsk, user, userId]);
  useEffect(() => {
    const p = panels[page];
    if (!p) return undefined;
    const unos = [...new Set((p.Buttons || [])
      .map((b) => toggleTarget(b)?.uniqueNumber || fanTarget(b)?.uniqueNumber)
      .filter(Boolean))];
    if (!unos.length) return undefined;

    connectMqtt();
    const detach = addMessageListener((topic, payload) => {
      const t = String(topic);
      if (!t.endsWith('/client')) return;
      const uno = t.slice(0, t.indexOf('/'));
      if (unos.includes(uno)) setStatuses((cur) => ({ ...cur, [uno]: String(payload) }));
    });
    unos.forEach((u) => subscribeTopic(`${u}/client`));
    return () => {
      detach();
      unos.forEach((u) => unsubscribeTopic(`${u}/client`));
    };
  }, [panels, page]);

  // Keep screen awake.
  useEffect(() => {
    let released = false;
    const acquire = async () => {
      try {
        if ('wakeLock' in navigator) wakeRef.current = await navigator.wakeLock.request('screen');
      } catch (e) { /* not supported / denied */ }
    };
    acquire();
    const onVis = () => { if (document.visibilityState === 'visible') acquire(); };
    document.addEventListener('visibilitychange', onVis);
    // Best-effort landscape lock (supported on some Android browsers in fullscreen).
    try { window.screen?.orientation?.lock?.('landscape').catch(() => {}); } catch (e) { /* ignore */ }
    return () => {
      released = true;
      document.removeEventListener('visibilitychange', onVis);
      try { wakeRef.current?.release?.(); } catch (e) { /* ignore */ }
      try { window.screen?.orientation?.unlock?.(); } catch (e) { /* ignore */ }
      if (released) wakeRef.current = null;
    };
  }, []);

  const goToPanelId = (panelId) => {
    const idx = panels.findIndex((p) => p.WallPanelId === panelId);
    if (idx >= 0) setPage(idx);
  };

  const onButton = (b, isOn) => {
    if (b.ButtonType === 'navigate') { goToPanelId(b.TargetWallPanelId); return; }
    // A toggle sends the opposite of what it is showing. Everything else runs
    // its stored sequence unchanged — curtains and scenes are fire-and-forget.
    if (toggleTarget(b)) { pressButton(b, { userId, isOn }); return; }
    runSequence(userId, b);
  };

  const exitWithPin = () => {
    const entered = window.prompt('Enter PIN to exit Wall Mode');
    if (entered == null) return;
    if (entered === String(user?.Password)) navigate('/home');
    else window.alert('Wrong PIN');
  };

  if (loading) {
    return <Box sx={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#000' }}><CircularProgress /></Box>;
  }

  if (panels.length === 0) {
    return (
      <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2, bgcolor: colors.back }}>
        <Typography sx={{ color: colors.text }} variant="h5">No wall panels yet</Typography>
        <Button variant="contained" onClick={() => navigate('/wall/design')}>Design a panel</Button>
        <Button onClick={exitWithPin} sx={{ color: colors.text }}>Exit</Button>
      </Box>
    );
  }

  const panel = panels[page];


  // A LOCATION panel is a fixed sheet: ColumnsCount holds the BUTTON COUNT and
  // the grid is derived from it, with every slot rendered whether or not it has
  // a button — the layout must not reflow as buttons are added, because muscle
  // memory on a wall panel is the entire point.
  //
  // A personal panel keeps the original free-flow behaviour, where ColumnsCount
  // really is a column count.
  const asGrid = isLocationPanel(panel);
  const size = asGrid ? normaliseSize(panel.ColumnsCount) : 0;
  const cols = asGrid ? columnsFor(size) : Math.max(1, panel.ColumnsCount);
  const gridSlots = asGrid ? layoutButtons(panel.Buttons, size) : null;
  const title = asGrid
    ? (parseLocationPanel(panel.PanelName)?.name || panel.PanelName)
    : panel.PanelName;

  return (
    // Fixed column: title row, grid taking the rest, pager pinned. Nothing
    // scrolls — a wall-mounted screen has no scrollbar anyone can reach.
    <Box sx={{
      height: '100vh', bgcolor: panel.BackColor || colors.back,
      position: 'relative', overflow: 'hidden',
      display: 'flex', flexDirection: 'column',
    }}
    >
      {/* Top bar */}
      <Box sx={{ display: 'flex', alignItems: 'center', px: 1, py: 0.5 }}>
        <Typography sx={{ flex: 1, color: colors.text, fontWeight: 600, pl: 1 }}>{title}</Typography>
        <IconButton onClick={() => setScreenOff(true)} sx={{ color: colors.text }}><PowerSettingsNewIcon /></IconButton>
        <IconButton onClick={async () => { navigate('/wall/design'); }} sx={{ color: colors.text }}><DashboardCustomizeIcon /></IconButton>
        <IconButton onClick={exitWithPin} sx={{ color: colors.text }}><CloseIcon /></IconButton>
      </Box>

      {/* Button grid */}
      <Box sx={{
        display: 'grid', gap: 1.5, p: 1.5,
        gridTemplateColumns: `repeat(${cols}, 1fr)`,
        // A fixed sheet fills the screen exactly — equal rows, no scrolling.
        // The tablet is on a wall; anything below the fold is unreachable.
        // The sheet takes whatever height is left and divides it evenly. Tiles
        // shrink to the screen; the screen never grows to the tiles.
        ...(asGrid
          ? { gridTemplateRows: `repeat(${rowsFor(size)}, 1fr)`, flex: 1, minHeight: 0, overflow: 'hidden' }
          : { gridAutoRows: '90px', flex: 1, minHeight: 0, overflowY: 'auto' }),
        pb: 5,
      }}>
        {asGrid ? gridSlots.map((b, i) => {
          const target = b ? toggleTarget(b) : null;
          const on = target ? isToggleOn(b, statuses[target.uniqueNumber]) : null;
          const fan = b ? fanTarget(b) : null;

          const light = b ? lightTarget(b) : null;
          // A colour light is its presets — one on/off button would throw away
          // every colour the product exists for.
          if (light) {
            const presets = lightPresetsFor(light.typeKey);
            return (
              <Cluster
                key={b.WallButtonId}
                button={b}
                steps={presets.map((pr) => ({ label: pr.label, message: pr.command }))}
                columns={presets.length > 4 ? 3 : 2}
                rows={Math.ceil(presets.length / (presets.length > 4 ? 3 : 2))}
                onStep={(m) => publishCommand(light.uniqueNumber, m, userId)}
              />
            );
          }

          const curtain = b ? curtainTarget(b) : null;
          const lock = b ? lockTarget(b) : null;

          // Open / Stop / Close belong together: Open without Stop is a gate you
          // cannot halt half-way. One slot, three actions.
          if (curtain) {
            return (
              <Cluster
                key={b.WallButtonId}
                button={b}
                steps={CURTAIN_ACTIONS.map((a) => ({ label: a.label, message: a.message }))}
                columns={3}
                rows={1}
                onStep={(m) => publishCommand(curtain.uniqueNumber, m, userId)}
              />
            );
          }

          // A lock is the one button that must not be pressable in passing —
          // the panel is by the door, and anyone standing at it can reach it.
          if (lock) {
            return (
              <Box
                key={b.WallButtonId}
                onClick={() => setLockAsk({ button: b, target: lock })}
                sx={{
                  bgcolor: b.ButtonColor || '#5D4037', color: b.TextColor || '#fff',
                  borderRadius: 2, display: 'flex', alignItems: 'center',
                  justifyContent: 'center', flexDirection: 'column', gap: 0.25,
                  p: 1, cursor: 'pointer', userSelect: 'none', fontWeight: 600,
                  minHeight: 0, minWidth: 0, overflow: 'hidden',
                  fontSize: 'clamp(12px, 2.2vw, 22px)',
                }}
              >
                <LockIcon sx={{ fontSize: '1.4em' }} />
                {b.ButtonText}
              </Box>
            );
          }

          // A fan takes the whole cell: six steps in one tile, so the ladder is
          // reachable without giving up five more slots to it.
          if (fan) {
            const speed = fanSpeedOf(b, statuses[fan.uniqueNumber]);
            // The ladder is built for THIS fan's channel — see fanStepsFor.
            const steps = fanStepsFor(fan.channel);
            return (
              <Cluster
                key={b.WallButtonId}
                button={b}
                steps={steps}
                columns={3}
                // Derived, not fixed at 2: a panel fan has nine buttons (off
                // plus eight steps) where a standalone fan has six, and a
                // hardcoded two rows clipped the top three off.
                rows={Math.ceil(steps.length / 3)}
                activeIndex={steps.findIndex((st) => st.speed === speed)}
                onStep={(m) => publishCommand(fan.uniqueNumber, m, userId)}
              />
            );
          }

          return (
            <Box
              // eslint-disable-next-line react/no-array-index-key
              key={b ? b.WallButtonId : `empty-${i}`}
              onClick={() => b && onButton(b, on)}
              sx={{
                bgcolor: b ? (b.ButtonColor || '#37474F') : 'rgba(255,255,255,0.04)',
                color: b ? (b.TextColor || '#fff') : 'transparent',
                borderRadius: 2, display: 'flex', alignItems: 'center',
                justifyContent: 'center', flexDirection: 'column',
                textAlign: 'center', p: 1, userSelect: 'none', fontWeight: 600,
                cursor: b ? 'pointer' : 'default',
                minHeight: 0, minWidth: 0, overflow: 'hidden',
                fontSize: 'clamp(12px, 2.2vw, 22px)',
                // A toggle shows its load. UNKNOWN is dimmed rather than drawn
                // as off — a sheet that shows everything off invites someone to
                // press what is already on.
                opacity: target && on === null ? 0.45 : 1,
                outline: on === true ? '3px solid rgba(255,255,255,0.9)' : 'none',
                outlineOffset: '-3px',
              }}
            >
              {b ? b.ButtonText : ''}
              {target && (
                <Box component="span" sx={{ fontSize: '0.6em', fontWeight: 500, opacity: 0.85 }}>
                  {/* eslint-disable-next-line no-nested-ternary */}
                  {on === null ? '—' : on ? 'ON' : 'OFF'}
                </Box>
              )}
            </Box>
          );
        }) : panel.Buttons.map((b) => (

          <Box
            key={b.WallButtonId}
            onClick={() => onButton(b)}
            sx={{
              gridColumn: `span ${Math.min(b.Width, cols)}`,
              gridRow: `span ${b.Height}`,
              bgcolor: b.ButtonColor || '#37474F',
              color: b.TextColor || '#fff',
              borderRadius: 2, display: 'flex', alignItems: 'center', justifyContent: 'center',
              textAlign: 'center', p: 1, cursor: 'pointer', userSelect: 'none', fontWeight: 600,
            }}
          >
            {b.ButtonType === 'navigate' ? `▶ ${b.ButtonText}` : b.ButtonText}
          </Box>
        ))}
      </Box>

      {/* Pager */}
      <Box sx={{ position: 'absolute', bottom: 6, left: 0, right: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
        <IconButton size="small" disabled={page === 0} onClick={() => setPage((p) => p - 1)} sx={{ color: colors.text }}><ChevronLeftIcon /></IconButton>
        {panels.map((_, i) => (
          <Box key={i} sx={{ width: i === page ? 18 : 6, height: 6, borderRadius: 3, bgcolor: i === page ? colors.button : 'rgba(255,255,255,0.4)' }} />
        ))}
        <IconButton size="small" disabled={page === panels.length - 1} onClick={() => setPage((p) => p + 1)} sx={{ color: colors.text }}><ChevronRightIcon /></IconButton>
      </Box>

      {/* Tap-to-wake overlay */}
      {screenOff && (
        <Box onClick={() => setScreenOff(false)} sx={{ position: 'absolute', inset: 0, bgcolor: '#000', zIndex: 10 }} />
      )}
    </Box>
  );
}
